import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Camera, ArrowRight, Loader2, CalendarDays, Clock, MapPin, ShieldCheck } from "lucide-react";

import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ASSETS } from "@/data/assets";
import { EVENT, FAQ } from "@/data/event";
import CheckoutDialog from "@/components/pp/CheckoutDialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ACCENTS = ["#E32636", "#175CFF", "#39E75F", "#8A35F2"];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const Polaroid = ({ src, caption, rotate = 0, className = "", floatDelay = 0 }) => (
  <div className={`polaroid float-img ${className}`} style={{ transform: `rotate(${rotate}deg)`, "--float-delay": `${-floatDelay}s` }}>
    <img src={src} alt={caption || "P&P"} />
    {caption && (
      <div className="text-[#090909] font-accent text-center text-sm pt-1 tracking-wide">{caption}</div>
    )}
  </div>
);

// Cursor-reactive floating Polaroid holding a neon pole silhouette. Tilts in 3D.
function FloatingDancer({ smx, smy, src, caption, color, depth = 1, className = "", delay = 0, rotate = 0 }) {
  const x = useTransform(smx, (v) => v * depth * 70);
  const y = useTransform(smy, (v) => v * depth * 45);
  const ry = useTransform(smx, (v) => v * depth * 16);
  const rx = useTransform(smy, (v) => -v * depth * 12);
  return (
    <motion.div style={{ x, y, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className={className}
      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.3, delay, ease: [0.22, 1, 0.36, 1] }}>
      <motion.div animate={{ y: [0, -16, 0] }}
        transition={{ duration: 6 + delay * 2, repeat: Infinity, ease: "easeInOut" }}
        style={{ transform: `rotate(${rotate}deg)` }}>
        <div className="polaroid" style={{ boxShadow: `0 30px 70px rgba(0,0,0,.65), 0 0 44px ${color}` }}>
          <div className="overflow-hidden bg-black" style={{ aspectRatio: "3 / 4" }}>
            <img src={src} alt="" draggable={false} className="w-full h-full object-cover select-none" />
          </div>
          <div className="text-center font-accent tracking-[0.35em] text-sm pt-1.5" style={{ color: color.replace(/,[^,]*\)/, ",1)") }}>
            {caption}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Landing() {
  const [config, setConfig] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [sponsorOpen, setSponsorOpen] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  // cursor-driven 3D parallax for the hero stage
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 50, damping: 18 });
  const smy = useSpring(my, { stiffness: 50, damping: 18 });
  const onHeroMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  useEffect(() => {
    const load = () => api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 20000); // live ticker: refresh spots every 20s
    return () => clearInterval(id);
  }, []);

  const salesOpen = config?.sales_open !== false;
  const openCheckout = () => {
    if (!config) {
      toast.error("Ticket availability is temporarily unavailable. Please try again.");
      return;
    }
    if (!config.sales_open) {
      window.location.assign("/gallery");
      return;
    }
    setCheckoutOpen(true);
  };

  const remaining = config?.remaining ?? 0;
  const heroLines = ["FOTOS ON THE GEAUX PRESENTS"];

  return (
    <div className="bg-[#090909] text-[#F5F0E8] overflow-hidden pb-24 md:pb-0">
      {/* NAV */}
      <nav className="fixed inset-x-0 top-0 z-50 max-w-full overflow-x-clip border-b border-white/5 bg-[#090909]/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="font-accent text-xl tracking-widest">P&amp;P<span className="neon-red">.</span></div>
          <div className="hidden md:flex items-center gap-8 font-accent text-sm tracking-widest text-[#F5F0E8]/70">
            <a href="#experience" className="hover:text-[#39E75F] transition-colors">EXPERIENCE</a>
            <a href="#tickets" className="hover:text-[#39E75F] transition-colors">TICKETS</a>
            <a href="#faq" className="hover:text-[#39E75F] transition-colors">FAQ</a>
            <a href="#partner" className="hover:text-[#39E75F] transition-colors">PARTNER</a>
            <a href="/tour" className="hover:text-[#39E75F] transition-colors">2027 TOUR</a>
          </div>
          <button
            data-testid="nav-get-tickets"
            onClick={() => openCheckout()}
            className="font-accent text-sm tracking-widest bg-[#E32636] px-5 py-2 rounded-full glow-hover hover:bg-[#ff2f40] flex items-center gap-2"
          >
            {config && (
              <span className="w-1.5 h-1.5 rounded-full bg-[#39E75F] animate-pulse" />
            )}
            {salesOpen ? "GET TICKETS" : "VIEW FOTOS"}
          </button>
        </div>
      </nav>

      {/* LIVE SPOTS BADGE (site-wide) */}
      {config && salesOpen && (
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4 }}
          data-testid="live-spots-badge"
          onClick={() => openCheckout()}
          className="fixed bottom-5 left-5 z-40 cursor-pointer backdrop-blur-xl bg-[#0d0d0d]/80 border border-white/10 rounded-full pl-3 pr-4 py-2 hidden md:flex items-center gap-2.5 glow-hover hover:border-[#39E75F]"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${config.remaining > 0 ? "bg-[#39E75F] animate-ping" : "bg-[#E32636]"}`} />
            <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${config.remaining > 0 ? "bg-[#39E75F]" : "bg-[#E32636]"}`} />
          </span>
          <span className="font-accent tracking-widest text-sm">
            {config.remaining > 0
              ? <><span className="text-[#F5F0E8]" data-testid="live-remaining">{config.remaining}</span><span className="text-[#F5F0E8]/50"> / {config.capacity} SPOTS LEFT</span></>
              : <span className="neon-red">SOLD OUT</span>}
          </span>
        </motion.div>
      )}

      {/* MOBILE TICKET BAR */}
      <div className="fixed inset-x-0 bottom-0 z-40 max-w-full overflow-x-clip border-t border-white/10 bg-[#090909]/95 px-4 py-3 backdrop-blur-xl safe-bottom md:hidden">
        <button
          data-testid="mobile-sticky-get-tickets"
          onClick={() => openCheckout()}
          className="w-full min-h-12 rounded-full bg-[#E32636] px-5 py-3 font-accent text-lg tracking-widest text-[#F5F0E8] shadow-[0_0_32px_rgba(227,38,54,.28)]"
        >
          {salesOpen ? `GET TICKETS · FROM $${EVENT.fromPrice}` : "VIEW EVENT FOTOS"}
        </button>
      </div>

      {/* HERO — interactive 3D stage */}
      <section ref={heroRef} onMouseMove={onHeroMove}
        className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden"
        style={{ perspective: "1400px" }}>
        {/* ambient nightclub glow */}
        <div className="absolute inset-0 opacity-40" style={{
          background: "radial-gradient(55% 50% at 25% 25%, rgba(227,38,54,.30), transparent), radial-gradient(50% 45% at 82% 30%, rgba(23,92,255,.30), transparent), radial-gradient(45% 40% at 70% 85%, rgba(138,53,242,.25), transparent)"
        }} />
        {/* vertical light beams / poles */}
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none" style={{
          background: "repeating-linear-gradient(90deg, transparent, transparent 11%, rgba(245,240,232,.5) 11%, rgba(245,240,232,.5) 11.15%)"
        }} />

        {/* floating polaroids — far layer (parallax slow) */}
        <motion.div style={{ y: y1, transformStyle: "preserve-3d" }} className="absolute inset-0 pointer-events-none">
          <FloatingDancer smx={smx} smy={smy} src={ASSETS.dancerPurple} caption="MOVE" color="rgba(138,53,242,.6)" depth={0.4} delay={0.2} rotate={-6}
            className="absolute left-[1%] md:left-[5%] top-[12%] w-32 md:w-52 opacity-70" />
          <FloatingDancer smx={smx} smy={smy} src={ASSETS.dancerGreen} caption="PARTY" color="rgba(57,231,95,.55)" depth={0.6} delay={0.35} rotate={5}
            className="absolute right-[2%] md:right-[7%] bottom-[7%] w-28 md:w-48 opacity-75" />
        </motion.div>

        {/* floating polaroids — near layer (parallax fast, reacts strongly to cursor) */}
        <motion.div style={{ y: y2, transformStyle: "preserve-3d" }} className="absolute inset-0 pointer-events-none">
          <FloatingDancer smx={smx} smy={smy} src={ASSETS.dancerBlue} caption="POSE" color="rgba(23,92,255,.7)" depth={1.3} delay={0.15} rotate={4}
            className="absolute right-[3%] md:right-[12%] top-[9%] w-40 md:w-64 opacity-100" />
          <FloatingDancer smx={smx} smy={smy} src={ASSETS.dancerRed} caption="FLASH" color="rgba(227,38,54,.7)" depth={1.0} delay={0.3} rotate={-5}
            className="absolute left-[1%] md:left-[3%] bottom-[3%] w-36 md:w-56 opacity-100 hidden sm:block" />
        </motion.div>

        {/* cinematic vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(72% 72% at 50% 45%, transparent 42%, rgba(9,9,9,.82) 100%)"
        }} />

        <motion.div style={{ y: heroTextY }} className="hero-copy-panel relative z-10 max-w-7xl mx-auto px-5 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
            className="font-accent tracking-[0.4em] text-xs md:text-sm neon-red mb-4"
          >
            {heroLines[0]}
          </motion.p>
          <h1 className="w-full max-w-full font-display font-black leading-[0.84] tracking-tighter text-[clamp(3.75rem,15vw,9rem)] md:text-[clamp(5rem,11vw,9rem)]">
            <MaskLine delay={0.3}>POLE</MaskLine>
            <MaskLine delay={0.45} className="italic">&amp; POLAROIDS</MaskLine>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
            className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2"
          >
            <span className="font-accent text-2xl md:text-4xl tracking-widest neon-blue">SEPTEMBER 25, 2026</span>
            <span className="font-accent text-lg tracking-widest text-[#F5F0E8]/70">· FRIDAY · NEW ORLEANS</span>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.78 }}
            className="text-[#F5F0E8]/85 max-w-2xl mt-4 text-base md:text-lg leading-relaxed"
          >
            {EVENT.includes}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }}
            className="campaign-print-tagline font-display italic text-2xl md:text-4xl mt-8 max-w-2xl leading-tight"
          >
            The night in your hands.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
            className="font-accent tracking-[0.3em] text-sm md:text-base mt-5 text-[#F5F0E8]/70"
          >
            POSE. <span className="neon-red">FLASH.</span> PRINT. <span className="neon-green">REPEAT.</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25 }}
            className="flex flex-wrap gap-4 mt-9"
          >
            <button data-testid="hero-get-tickets" onClick={() => openCheckout()}
              className="font-accent tracking-widest text-lg bg-[#E32636] px-8 py-4 rounded-full glow-hover hover:bg-[#ff2f40] flex items-center gap-2">
              <Camera size={18} /> {salesOpen ? `GET TICKETS — FROM $${EVENT.fromPrice}` : "VIEW EVENT FOTOS"}
            </button>
            <a href="#experience" data-testid="hero-see-experience"
              className="font-accent tracking-widest text-lg border border-white/25 px-8 py-4 rounded-full glow-hover hover:border-[#39E75F] hover:text-[#39E75F] flex items-center gap-2">
              <ArrowRight size={18} /> SEE THE EXPERIENCE
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* EVENT INFO STRIP */}
      <div data-testid="event-info-strip" className="bg-[#0d0d0d] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 py-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center font-accent tracking-widest text-sm md:text-base">
          <span className="text-[#F5F0E8]">{EVENT.dateStrip}</span>
          <span className="text-[#F5F0E8]/30">·</span>
          <span className="neon-blue">{EVENT.time}</span>
          <span className="text-[#F5F0E8]/30">·</span>
          <span className="text-[#F5F0E8]/80">{EVENT.city.toUpperCase()}</span>
          <span className="text-[#F5F0E8]/30">·</span>
          <span className="neon-green">ONLY 20 GUESTS</span>
          <span className="text-[#F5F0E8]/30">·</span>
          <span className="text-[#F5F0E8]/80">{EVENT.age}</span>
          <span className="text-[#F5F0E8]/30">·</span>
          <span className="neon-red">FROM ${EVENT.fromPrice}</span>
        </div>
      </div>

      {/* MOVING COLLAGE */}
      <section className="py-10 border-y border-white/5">
        <Marquee gradient={false} speed={40} pauseOnHover>
          {[
            { s: ASSETS.polaroidGrid, c: "POSE" },
            { s: ASSETS.centerpiece, c: "SILHOUETTE" },
            { s: ASSETS.party1, c: "FLASH" },
            { s: ASSETS.flashProcess, c: "PRINT" },
            { s: ASSETS.party2, c: "PARTY" },
          ].map((p, i) => (
            <div key={i} className="w-56 md:w-72 mx-4">
              <Polaroid src={p.s} caption={p.c} rotate={i % 2 ? 2 : -2} floatDelay={i * 0.9} />
            </div>
          ))}
        </Marquee>
      </section>

      {/* WHAT IS THIS */}
      <section className="py-28 md:py-40 max-w-6xl mx-auto px-5">
        <Reveal>
          <div className="font-display leading-[0.95] tracking-tighter">
            <p className="text-4xl md:text-7xl">NOT A TRADITIONAL <span className="text-stroke">POLE CLASS.</span></p>
            <p className="text-4xl md:text-7xl mt-2">NOT A STANDARD <span className="text-stroke">PHOTOSHOOT.</span></p>
            <p className="text-3xl md:text-6xl mt-4 neon-green italic leading-tight">A guided movement, confidence &amp; portrait experience.</p>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 text-lg md:text-2xl max-w-2xl text-[#F5F0E8]/85 font-display italic">
            Come as you are. You'll move. You'll pose. You'll learn. You'll get photographed.
            And before you leave, you'll have physical Fotos from the night in your hands.
          </p>
          <p className="mt-4 font-accent tracking-widest neon-blue">NO POLE EXPERIENCE REQUIRED.</p>
          <p className="mt-3 font-display italic text-xl md:text-2xl text-[#F5F0E8]/85">
            Come solo or roll with your <span className="neon-green">krewe</span> — book a krewe of up to 4 people in one checkout.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-4">
            {[["1–4", "PER KREWE"], ["20", "GUESTS MAX"], ["2", "PRINTS INCLUDED"], ["2", "HOUR EXPERIENCE"], ["001", "FIRST EDITION"]].map(([n, l], i) => (
              <div key={i} className="border border-white/10 rounded-lg p-5">
                <div className="font-display text-5xl" style={{ color: ACCENTS[i % 4] }}>{n}</div>
                <div className="font-accent tracking-widest text-sm text-[#F5F0E8]/70 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Sequence */}
        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-wrap items-center gap-x-3 gap-y-2 font-accent text-2xl md:text-4xl tracking-widest">
            {["ARRIVE", "MOVE", "POSE", "FLASH", "PRINT", "PARTY"].map((w, i) => (
              <span key={w} className="flex items-center gap-3">
                <span style={{ color: ACCENTS[i % 4] }}>{w}</span>
                {i < 5 && <ArrowRight className="text-[#F5F0E8]/30" size={22} />}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* THE EXPERIENCE */}
      <section id="experience" className="py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal>
            <p className="font-accent tracking-[0.4em] neon-red text-sm">THE EXPERIENCE</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-3">Six moments. One night.</h2>
          </Reveal>
          <div className="mt-16 space-y-px">
            {[
              ["01", "Check In", "Music, lighting, introductions and your first look at the set."],
              ["02", "Pole + Movement", "Beginner-friendly instruction designed around movement, posing and confidence — not advanced pole fitness."],
              ["03", "Find Your Pose", "Work in small groups and rotate through the photographic setups."],
              ["04", "Flash", "Fotos on the Geaux captures the moment with the direct-flash, nightlife photography aesthetic."],
              ["05", "The Print", "Your Foto gets printed on-site and placed into the branded keepsake presentation."],
              ["06", "Back to the Party", "The night in your hands."],
            ].map(([n, t, d], i) => (
              <Reveal key={n} delay={i * 0.05}>
                <div className="group grid md:grid-cols-[120px_1fr] gap-4 md:gap-10 items-baseline py-8 border-b border-white/10 hover:border-[#39E75F]/50 transition-colors">
                  <div className="font-display text-6xl md:text-7xl text-stroke group-hover:text-[#F5F0E8] transition-colors">{n}</div>
                  <div>
                    <h3 className="font-display text-3xl md:text-4xl">{t}</h3>
                    <p className="text-[#F5F0E8]/75 mt-2 max-w-xl">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-10 font-display italic text-xl md:text-2xl text-[#F5F0E8]/75 max-w-3xl">
              JC directs → guest poses → flash → Foto prints → branded holder → guest returns to the experience.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE PRINT SHOWCASE */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="polaroid float-img max-w-md mx-auto" style={{ transform: "rotate(-3deg)", "--float-duration": "7s" }}>
              <img src={ASSETS.printHolder} alt="A rich-brown hand holding a physical Pole & Polaroids instant print" />
              <div className="text-[#090909] font-accent text-center text-sm pt-1 tracking-widest">THE KEEPSAKE</div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="font-accent tracking-[0.4em] neon-red text-sm">THE PRINT</p>
            <h2 className="campaign-print-tagline font-display text-4xl md:text-6xl tracking-tighter mt-3">The night in your hands.</h2>
            <p className="text-[#F5F0E8]/80 mt-5 text-lg leading-relaxed max-w-lg">
              This isn't a camera roll you forget about. Every guest leaves with two physical instant prints,
              printed on-site and placed into a branded POLE &amp; POLAROIDS keepsake holder — proof you were here.
            </p>
            <p className="text-[#F5F0E8]/60 mt-3">Want more? Add extra instant prints, a digital pack, or a 20×30 or 24×36 poster print at checkout—then choose your favorite photo after the event.</p>
          </Reveal>
        </div>
      </section>

      {/* HOST / TEAM */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal>
            <p className="font-accent tracking-[0.4em] neon-purple text-sm">WHO'S BEHIND THE LENS</p>
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-3">Your hosts for the night.</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {[
              { name: "JC", role: "Creative Director & Photographer", cred: "Founder and creative director of Fotos on the Geaux — the direct-flash nightlife eye behind the experience. JC directs every pose and captures the moment that becomes your print.", soon: false },
              { name: "Movement Instructor", role: "Pole & Movement Guide", cred: "Your beginner-friendly guide for the night, leading the movement, posing and confidence work. Announced soon.", soon: true },
            ].map((h, i) => (
              <Reveal key={h.name} delay={i * 0.1}>
                <div className={`border rounded-xl p-6 flex gap-5 items-center h-full ${h.soon ? "border-white/5 opacity-70" : "border-white/10"}`}>
                  <div className="w-24 h-28 shrink-0 rounded-md bg-gradient-to-br from-white/10 to-transparent border border-white/10 flex items-center justify-center" style={{ boxShadow: `0 0 30px ${ACCENTS[i]}33` }}>
                    <Camera size={26} style={{ color: ACCENTS[i] }} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl">{h.name}</h3>
                    <p className="font-accent tracking-widest text-xs mt-1" style={{ color: ACCENTS[i] }}>{h.role}</p>
                    <p className="text-[#F5F0E8]/70 text-sm mt-2">{h.cred}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TICKETS */}
      <section id="tickets" className="py-28 relative">
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(50% 40% at 50% 0%, rgba(57,231,95,.4), transparent)" }} />
        <div className="max-w-6xl mx-auto px-5 relative">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-accent tracking-[0.4em] neon-blue text-sm">TICKETS</p>
                <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-3">{salesOpen ? "Only 20 spots." : "The event is complete."}</h2>
                <p className="text-[#F5F0E8]/50 mt-2 text-sm">{salesOpen ? "Price advances by availability — the earlier you book, the less you pay. Come solo or book your krewe (up to 4 people) in one checkout." : "Ticket sales are closed. Head to the gallery to find and claim your event photos."}</p>
              </div>
              <div data-testid="spots-remaining" className="font-accent tracking-widest text-2xl md:text-3xl neon-green">
                {salesOpen ? `${remaining} SPOTS LEFT` : "FOTOS ARE READY"}
              </div>
            </div>
          </Reveal>

          {/* Event details near the first ticket button */}
          <Reveal>
            <div data-testid="event-details" className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { Icon: CalendarDays, label: "DATE", value: EVENT.dateLong },
                { Icon: Clock, label: "TIME", value: `${EVENT.time}`, sub: EVENT.doors },
                { Icon: MapPin, label: "VENUE", value: EVENT.venueName, sub: EVENT.venueAddress },
                { Icon: ShieldCheck, label: "ADMISSION", value: `${EVENT.age} · Valid ID`, sub: "2 hrs · 2 prints included" },
              ].map(({ Icon, label, value, sub }, i) => (
                <div key={label} className="border border-white/10 rounded-lg p-4 bg-[#0d0d0d]">
                  <Icon size={18} style={{ color: ACCENTS[i % 4] }} />
                  <div className="font-accent tracking-widest text-[10px] text-[#F5F0E8]/50 mt-2">{label}</div>
                  <div className="text-sm text-[#F5F0E8] mt-0.5">{value}</div>
                  {sub && <div className="text-xs text-[#F5F0E8]/50 mt-0.5">{sub}</div>}
                </div>
              ))}
            </div>
            <p className="text-[#F5F0E8]/75 mt-4 max-w-3xl leading-relaxed">{EVENT.includes}</p>
            <p className="text-[#F5F0E8]/50 text-sm mt-2 max-w-3xl">{EVENT.policies.refund}</p>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {config && Object.entries(config.tiers).map(([k, v], i) => {
              const isCurrent = k === config.current_tier;
              const label = k === "early_pose" ? "EARLY POSE" : k === "general" ? "GENERAL" : "LAST CALL";
              return (
              <Reveal key={k} delay={i * 0.1}>
                <div className={`border rounded-xl p-7 h-full flex flex-col glow-hover ${
                    v.sold_out ? "border-white/5 opacity-50"
                    : isCurrent ? "border-[#39E75F] hover:-translate-y-1"
                    : "border-white/10 hover:border-white/30 hover:-translate-y-1"}`}
                  style={{ background: "linear-gradient(160deg, rgba(255,255,255,0.03), transparent)" }}>
                  <div className="flex items-center justify-between">
                    <div className="font-accent tracking-widest text-sm" style={{ color: ACCENTS[i] }}>{label}</div>
                    {v.sold_out ? (
                      <span className="font-accent text-xs tracking-widest text-[#F5F0E8]/40">SOLD OUT</span>
                    ) : isCurrent ? (
                      <span className="font-accent text-xs tracking-widest neon-green">ON SALE NOW</span>
                    ) : (
                      <span className="font-accent text-xs tracking-widest text-[#F5F0E8]/40">UP NEXT</span>
                    )}
                  </div>
                  <div className="font-display text-6xl mt-3">${v.price}</div>
                  <p className="text-[#F5F0E8]/75 mt-3 flex-1">{v.blurb}</p>
                  <p className="text-xs mt-4" style={{ color: v.sold_out ? "rgba(245,240,232,.4)" : ACCENTS[i] }}>
                    {v.sold_out ? "All spots claimed" : `${v.remaining} of ${v.allocation} left`}
                  </p>
                  <button data-testid={`tier-select-${k}`} onClick={salesOpen && isCurrent ? openCheckout : undefined} disabled={!salesOpen || !isCurrent}
                    className="mt-4 w-full py-3 font-accent tracking-widest rounded-full border border-white/20 hover:bg-[#F5F0E8] hover:text-[#090909] glow-hover disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#F5F0E8]">
                    {!salesOpen ? "EVENT COMPLETE" : v.sold_out ? "SOLD OUT" : isCurrent ? `RESERVE — $${v.price}` : "UP NEXT"}
                  </button>
                </div>
              </Reveal>
            );})}
          </div>
          {/* Add-ons preview */}
          <Reveal>
            <div className="mt-16">
              <p className="font-accent tracking-[0.3em] text-sm text-[#F5F0E8]/70 mb-4">MAKE IT MORE — CHECKOUT ADD-ONS</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {config && Object.entries(config.addons).map(([k, a], i) => (
                  <div key={k} className="border border-white/10 rounded-lg p-4">
                    <div className="font-medium">{a.name}</div>
                    <div className="font-display text-2xl mt-1" style={{ color: ACCENTS[i % 4] }}>${a.price}</div>
                    <div className="text-xs text-[#F5F0E8]/60 mt-1">{a.blurb}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VISUAL CENTERPIECE */}
      <section className="relative min-h-screen flex flex-col items-center justify-center text-center px-5 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ASSETS.centerpiece} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#090909] via-transparent to-[#090909]" />
        </div>
        <div className="relative z-10 space-y-8">
          <Reveal><p className="font-display text-4xl md:text-7xl tracking-tighter">COME FOR THE EXPERIENCE.</p></Reveal>
          <Reveal delay={0.15}><p className="font-display italic text-4xl md:text-7xl tracking-tighter neon-green">LEAVE WITH THE PROOF.</p></Reveal>
          <Reveal delay={0.3}><p className="campaign-print-tagline font-accent tracking-[0.3em] text-xl md:text-3xl neon-red">THE NIGHT IN YOUR HANDS.</p></Reveal>
        </div>
      </section>

      {/* POWERED BY FOTG */}
      <section className="py-28 border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 text-center">
          <Reveal>
            <p className="font-accent tracking-[0.4em] text-sm text-[#F5F0E8]/50">POWERED BY</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-2">FOTOS ON THE GEAUX</h2>
            <p className="text-[#F5F0E8]/80 max-w-2xl mx-auto mt-5 text-lg">
              Fotos on the Geaux turns events into physical memories guests can take with them immediately.
            </p>
            <p className="font-display italic text-2xl mt-8">Want Fotos on the Geaux at your event?</p>
            <a href="https://geauxfotos.com" target="_blank" rel="noreferrer" data-testid="book-moment-btn"
              className="inline-flex items-center gap-2 mt-6 font-accent tracking-widest text-lg border border-white/25 px-8 py-4 rounded-full glow-hover hover:border-[#175CFF] hover:text-[#175CFF]">
              <Camera size={18} /> BOOK YOUR MOMENT
            </a>
          </Reveal>
        </div>
      </section>

      {/* SPONSORS */}
      <section id="partner" className="py-28 border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal>
            <p className="font-accent tracking-[0.4em] neon-green text-sm">PARTNER WITH THE EXPERIENCE</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-3">Become a Partner.</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {config && config.sponsor_tiers.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.05}>
                <div className="border border-white/10 rounded-lg p-6 h-full glow-hover hover:border-[#39E75F]">
                  <div className="font-display text-2xl" style={{ color: ACCENTS[i % 4] }}>{t.name}</div>
                  <p className="text-[#F5F0E8]/70 text-sm mt-2">{t.benefit}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="/tour"
                className="font-accent tracking-widest text-lg border border-white/20 px-8 py-4 rounded-full glow-hover hover:border-[#175CFF] hover:text-[#175CFF]">
                EXPLORE THE 2027 TOUR
              </a>
              <button data-testid="become-partner-btn" onClick={() => setSponsorOpen(true)}
                className="font-accent tracking-widest text-lg bg-[#39E75F] text-[#090909] px-8 py-4 rounded-full glow-hover hover:bg-[#5cff7e]">
                BECOME A PARTNER
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FINAL CONVERSION */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={ASSETS.party1} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-[#090909]/70" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-5 text-center">
          <Reveal>
            <p className="font-accent tracking-[0.3em] text-xl neon-blue">SEPTEMBER 25</p>
            <div className="font-display tracking-tighter mt-4 leading-[0.95]">
              <p className="text-3xl md:text-5xl">20 spots. One camera.</p>
              <p className="text-3xl md:text-5xl">Come solo or bring your krewe.</p>
            </div>
            <p className="campaign-print-tagline font-display italic text-2xl md:text-4xl mt-10">
              The night in your hands.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <button data-testid="final-reserve-btn" onClick={() => openCheckout()}
                className="font-accent tracking-widest text-lg bg-[#E32636] px-8 py-4 rounded-full glow-hover hover:bg-[#ff2f40]">
                {salesOpen ? "RESERVE MY SPOT — FROM $35" : "VIEW EVENT FOTOS"}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WAITLIST */}
      <WaitlistBlock />

      {/* FAQ */}
      <section id="faq" className="py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-5">
          <Reveal>
            <p className="font-accent tracking-[0.4em] neon-blue text-sm">GOOD TO KNOW</p>
            <h2 className="font-display text-5xl md:text-7xl tracking-tighter mt-3">FAQ</h2>
          </Reveal>
          <Reveal>
            <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
              {FAQ.map(([q, a], i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-display text-lg md:text-2xl hover:no-underline text-[#F5F0E8]">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#F5F0E8]/75 text-base leading-relaxed">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14 border-t border-white/5 pb-24 md:pb-14">
        <div className="max-w-6xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-4 text-[#F5F0E8]/50 text-sm">
          <div className="font-accent tracking-widest text-[#F5F0E8]">POLE &amp; POLAROIDS <span className="neon-red">·</span> BY FOTOS ON THE GEAUX</div>
          <span>© 2026 Fotos on the Geaux · New Orleans</span>
        </div>
      </footer>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} config={config} />
      <SponsorDialog open={sponsorOpen} onOpenChange={setSponsorOpen} tiers={config?.sponsor_tiers || []} />
    </div>
  );
}

function MaskLine({ children, delay = 0, className = "" }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

function WaitlistBlock() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Enter your email.");
    setLoading(true);
    try {
      await api.post("/waitlist", { ...form, ref: localStorage.getItem("pp_ref") || "direct" });
      setForm({ name: "", email: "", phone: "" });
      window.location.assign("/waitlist/success");
    } catch {
      toast.error("Something went wrong. Try again.");
    }
    setLoading(false);
  };
  return (
    <section className="py-24 bg-[#0d0d0d]">
      <div className="max-w-3xl mx-auto px-5 text-center">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter">CAN'T MAKE THIS ONE?</h2>
          <p className="text-[#F5F0E8]/60 mt-3">Get invited to the next POLE &amp; POLAROIDS.</p>
          <form onSubmit={submit} className="mt-8 grid sm:grid-cols-[1fr_1fr_auto] gap-3">
            <Input data-testid="waitlist-email" type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-transparent border-white/15 text-[#F5F0E8]" />
            <Input data-testid="waitlist-phone" placeholder="Phone" value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="bg-transparent border-white/15 text-[#F5F0E8]" />
            <button data-testid="waitlist-submit" disabled={loading}
              className="font-accent tracking-widest bg-[#175CFF] px-8 py-2 rounded-md glow-hover hover:bg-[#3d76ff] flex items-center justify-center gap-2">
              {loading ? <Loader2 className="animate-spin" size={16} /> : "JOIN"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function SponsorDialog({ open, onOpenChange, tiers }) {
  const [f, setF] = useState({
    company: "", contact_name: "", email: "", instagram_or_site: "",
    tier: "", contribution: "", contribution_type: "product", desired_activation: "",
  });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!f.company || !f.contact_name || !f.email || !f.tier || !f.contribution) {
      return toast.error("Fill company, contact, email, tier and contribution.");
    }
    setLoading(true);
    try {
      await api.post("/sponsors", f);
      onOpenChange(false);
      setF({ company: "", contact_name: "", email: "", instagram_or_site: "", tier: "", contribution: "", contribution_type: "product", desired_activation: "" });
      window.location.assign("/partner/success");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };
  const S = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sponsor-dialog" className="bg-[#0d0d0d] border-white/10 text-[#F5F0E8] max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-accent text-2xl tracking-widest">PARTNER WITH THE EXPERIENCE</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Input data-testid="sponsor-company" placeholder="Company" value={f.company} onChange={S("company")} className="bg-transparent border-white/15" />
          <Input data-testid="sponsor-contact" placeholder="Contact name" value={f.contact_name} onChange={S("contact_name")} className="bg-transparent border-white/15" />
          <Input data-testid="sponsor-email" type="email" placeholder="Email" value={f.email} onChange={S("email")} className="bg-transparent border-white/15" />
          <Input data-testid="sponsor-social" placeholder="Instagram / website" value={f.instagram_or_site} onChange={S("instagram_or_site")} className="bg-transparent border-white/15" />
          <Select value={f.tier} onValueChange={(v) => setF({ ...f, tier: v })}>
            <SelectTrigger data-testid="sponsor-tier" className="bg-transparent border-white/15"><SelectValue placeholder="Desired sponsor tier" /></SelectTrigger>
            <SelectContent className="bg-[#0d0d0d] text-[#F5F0E8] border-white/15">
              {tiers.map((t) => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.contribution_type} onValueChange={(v) => setF({ ...f, contribution_type: v })}>
            <SelectTrigger data-testid="sponsor-type" className="bg-transparent border-white/15"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0d0d0d] text-[#F5F0E8] border-white/15">
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="product">Product</SelectItem>
              <SelectItem value="service">Service</SelectItem>
            </SelectContent>
          </Select>
          <Textarea data-testid="sponsor-contribution" placeholder="Proposed contribution" value={f.contribution} onChange={S("contribution")} className="bg-transparent border-white/15" />
          <Textarea data-testid="sponsor-activation" placeholder="Desired activation" value={f.desired_activation} onChange={S("desired_activation")} className="bg-transparent border-white/15" />
          <button data-testid="sponsor-submit" onClick={submit} disabled={loading}
            className="font-accent tracking-widest bg-[#39E75F] text-[#090909] py-3 rounded-md glow-hover hover:bg-[#5cff7e] flex items-center justify-center gap-2">
            {loading ? <Loader2 className="animate-spin" size={16} /> : "SUBMIT PARTNERSHIP INQUIRY"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}